/**
 * Suche des Zeitstrahls - Nachportierung von `timelineOrderMatchesSearch` (`index.html:13718`) samt
 * seines Zulieferers `timelineOrderSearchValues` (`index.html:13703`).
 *
 * ================= Die eine Aenderung gegenueber dem Original =================
 *
 * Spec, Verhaltensaenderung 6 bzw. "Testing-Ansatz - Zeitstrahl": „mit dem Umzug sucht der
 * Tracking-Teil ueber die Sendungsentitaet statt ueber das entfallende Feld `trackingNo`". Im
 * Original steht die Sendungsnummer als Feld AN DER BESTELLUNG (`order?.trackingNo`,
 * `index.html:13706`); nach dem Umzug traegt sie die verknuepfte Sendung (`sendung.trackingNo`).
 *
 * Alles andere ist woertlich: dieselben Felder, dieselbe Reihenfolge, dieselbe Normalisierung
 * (`normSearch`), dasselbe `!query || ...`-Kurzschlussverhalten (leere Suche trifft alles).
 *
 * FOLGE, bewusst und im Task-Bericht gemeldet: eine Bestellung, die noch ein Alt-Feld `trackingNo`
 * traegt, wird darueber NICHT mehr gefunden. Das ist genau die beabsichtigte Aenderung - das Feld
 * entfaellt. Der Differenztest (`test/parity/suche.test.ts`) fuehrt beide Richtungen: fuer Daten
 * ohne `trackingNo` sind Original und Portierung ohne Sendung identisch, fuer Daten MIT `trackingNo`
 * weichen sie an genau dieser einen Stelle ab.
 *
 * ================= Auftrag 2: Bestellname und Artikelname =================
 *
 * `order?.name` und `item?.name` kommen seit Auftrag 2 ("Zeitstrahlseite: sprechende Zeile,
 * Artikelstreifen, Positions-Popup") dazu - beide stehen jetzt sichtbar in der Zeile bzw. im
 * Positionen-Popup, wonach der Nutzer sieht, muss er auch suchen koennen. Kein Original-Vorbild
 * (`timelineOrderSearchValues`, index.html:13703, kennt keins von beiden) - fuer den Differenztest
 * folgenlos: keine der dortigen Fixturen traegt ein `name`-Feld, ein zusaetzlicher `undefined`-Eintrag
 * in der Werteliste findet nie einen Treffer und aendert an keinem bestehenden Ergebnis etwas.
 *
 * ================= Der dritte Parameter: eine Sendung, keine Liste (Auftrag „Sendungsabruf beim
 * App-Start entfernen", 2026-08-22) =================
 *
 * Bis hierher hiess der dritte Parameter `sendungen` (eine Liste) und eine modulinterne
 * Aufloesefunktion suchte ueber `order.shipmentId` darin - EIN Feld, das weder der
 * `Order`-Typ fuehrt noch irgendeine Route je liefert. Die echte Verknuepfung steht in
 * `order_shipments` und wird ausschliesslich je Bestellung gelesen (`GET
 * /api/orders/:orderId/shipment`, `api/sendungen.ts#sendungZuBestellungLaden`) - nie als Liste ueber
 * alle Bestellungen (ein solcher Sammelabruf existiert im Backend nicht und waere aus dieser Route
 * auch nicht formgleich zusammensetzbar, siehe Kopfkommentar `api/sendungen.ts`). Diese Datei nimmt
 * seither die BEREITS AUFGELOESTE Sendung GENAU DIESER EINEN Bestellung entgegen (`sendung: Sendung
 * | null | undefined`), nicht mehr eine Liste, aus der sie sich eine heraussuchen muesste - der
 * Aufrufer (`ZeitstrahlSeite.tsx`) reicht sie unveraendert an `zeitstrahlSuchwerte` durch.
 *
 * Ausfuehrlich, samt der erwogenen Alternativen: `docs/superpowers/decisions/2026-08-22-sendungen-app-start.md`.
 */
import { normSearch } from "./format.ts";
import type { Order, Sendung } from "./types.ts";

/**
 * index.html:13703 (`timelineOrderSearchValues`). Modulintern wie im Original; die einzige Aenderung
 * ist der zweite Eintrag - siehe Kopfkommentar.
 */
function zeitstrahlSuchwerte(
  order: Order | null | undefined,
  sendung: Sendung | null | undefined,
): unknown[] {
  return [
    order?.invoiceNo,
    sendung?.trackingNo,
    order?.orderNo,
    order?.supplier,
    order?.name,
    ...((order?.items || []) as Record<string, unknown>[]).flatMap((item) => [
      item?.sku,
      item?.articleNo,
      item?.articleNumber,
      item?.itemNo,
      item?.ean,
      item?.name,
    ]),
  ];
}

/**
 * index.html:13718 (`timelineOrderMatchesSearch`). Der Vorgabewert des Originals (`value =
 * timelineSearch`) faellt weg - `timelineSearch` ist Oberflaechenzustand und liegt in der SPA im
 * `useState` der Seite, nicht als Modul-Global.
 */
export function zeitstrahlTrifftSuche(
  order: Order | null | undefined,
  suchbegriff: unknown,
  sendung: Sendung | null | undefined,
): boolean {
  const query = normSearch(suchbegriff);
  return (
    !query ||
    zeitstrahlSuchwerte(order, sendung).some((reference) => normSearch(reference).includes(query))
  );
}
